/**
 * Custom Hook for One-to-One Chat with PubNub
 * Production-ready optimized version with fixed infinite loop issue
 */

import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import PubNub from "pubnub";
import { usePubNub } from "pubnub-react";
import { generateOneToOneChannel, CHANNEL_PREFIXES } from "./config";
import {
  ChatMessage,
  ChatFileData,
  MessageReaction,
  MessageReactionsMap,
  MessageReceiptsMap,
  MessageReceipt,
  ReplyTo,
} from "./types";

// Matches the shape from the UI component
interface FileWithPreview {
  file: File;
  preview: string | null;
  id: string;
}
import { sendMessageService, readChatService } from "src/services";

// Constants for optimization
const TYPING_DEBOUNCE_MS = 1000;
const TYPING_TIMEOUT_MS = 5000;
const PRESENCE_CHECK_INTERVAL_MS = 30000;
const INITIAL_HISTORY_COUNT = 25;
const PAGE_SIZE = 25;
const MAX_LOAD_ATTEMPTS = 40;
const MESSAGE_CACHE_SIZE = 1000;
const DELIVERY_RECEIPT_BATCH_MS = 500; // Batch delivery receipts

interface UseOneToOneChatOptions {
  currentUserId: string;
  targetUserId: string;
  currentUserName: string;
  autoSubscribe?: boolean;
  historyCount?: number;
  currentRoomId?: string;
  channelId?: string; // override the generated channel (e.g. admin viewing ongoing chat)
  typingDebounceMs?: number;
  enableMessageCaching?: boolean;
  autoSendDeliveredReceipts?: boolean; // Add option to control auto receipts
}

interface UiState {
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  isConnected: boolean;
  isTargetOnline: boolean;
  isTargetTyping: boolean;
}

interface UseOneToOneChatReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  isConnected: boolean;
  isTargetOnline: boolean;
  isTargetTyping: boolean;
  reactions: MessageReactionsMap;
  receipts: MessageReceiptsMap;
  deletedForMe: Set<string>;
  sendMessage: (text: string, files?: File[] | FileWithPreview[], replyTo?: ReplyTo) => Promise<void>;
  editMessage: (messageId: string, newText: string) => Promise<void>;
  deleteMessage: (messageId: string, deleteForEveryone: boolean) => Promise<void>;
  sendTypingIndicator: (isTyping: boolean) => void;
  addReaction: (messageTimetoken: string, emoji: string) => Promise<void>;
  removeReaction: (messageTimetoken: string, actionTimetoken: string) => Promise<void>;
  markAsRead: (messageTimetoken: string) => void;
  getMessageStatus: (msg: ChatMessage) => any;
  loadMoreMessages: () => Promise<void>;
  loadMessagesToTimetoken: (targetTimetoken: string) => Promise<boolean>;
  clearMessages: () => void;
  channelId: string;
  retryFailedMessage: (messageId: string) => Promise<void>;
  clearError: () => void;
}

/**
 * Production-ready optimized hook for managing one-to-one chat with PubNub
 */
export const useOneToOneChat = ({
  currentUserId,
  targetUserId,
  currentUserName,
  autoSubscribe = true,
  historyCount = INITIAL_HISTORY_COUNT,
  currentRoomId = "",
  channelId: channelIdOverride,
  typingDebounceMs = TYPING_DEBOUNCE_MS,
  enableMessageCaching = true,
  autoSendDeliveredReceipts = true // Default to true but configurable
}: UseOneToOneChatOptions): UseOneToOneChatReturn => {
  const pubnub = usePubNub();

  // Core state
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [reactions, setReactions] = useState<MessageReactionsMap>({});
  const [receipts, setReceipts] = useState<MessageReceiptsMap>({});
  const [deletedForMe, setDeletedForMe] = useState<Set<string>>(new Set());

  // UI state grouped for batch updates
  const [uiState, setUiState] = useState<UiState>({
    isLoading: false,
    isLoadingMore: false,
    hasMore: true,
    error: null,
    isConnected: true,
    isTargetOnline: false,
    isTargetTyping: false
  });

  // Refs for values that don't need re-renders
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sentReceiptsRef = useRef<Set<string>>(new Set()); // Track sent receipts to avoid duplicates
  const pendingReceiptsRef = useRef<Set<string>>(new Set()); // Track receipts being processed
  const oldestTimetokenRef = useRef<string | null>(null);
  const messageCacheRef = useRef<Map<string, ChatMessage>>(new Map());
  const pendingTypingRef = useRef<boolean>(false);
  const lastTypingSentRef = useRef<number>(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const pendingRequestsRef = useRef<Set<string>>(new Set());
  const processedMessageTimetokensRef = useRef<Set<string>>(new Set()); // Track processed messages for receipts
  const receiptsBatchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingReceiptsBatchRef = useRef<Set<string>>(new Set()); // Batch pending receipts

  // Memoized channel IDs
  const channelId = useMemo(() =>
    channelIdOverride || generateOneToOneChannel(currentUserId, targetUserId),
    [channelIdOverride, currentUserId, targetUserId]
  );

  const typingChannel = useMemo(() =>
    `${channelId}-typing`,
    [channelId]
  );

  // Cleanup function for abort controller
  const cleanupAbortController = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setUiState(prev => ({ ...prev, error: null }));
  }, []);

  // Generate unique message ID
  const generateMessageId = useCallback((): string => {
    return `${currentUserId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${crypto.randomUUID?.() || Date.now()}`;
  }, [currentUserId]);

  // Message cache management
  const cacheMessage = useCallback((message: ChatMessage) => {
    if (!enableMessageCaching || !message.timetoken) return;

    const timetokenStr = String(message.timetoken);

    if (messageCacheRef.current.size >= MESSAGE_CACHE_SIZE) {
      // Remove oldest entry (first key)
      const firstKey = messageCacheRef.current.keys().next().value;
      if (firstKey !== undefined) {
        messageCacheRef.current.delete(firstKey);
      }
    }
    messageCacheRef.current.set(timetokenStr, message);
  }, [enableMessageCaching]);

  const getCachedMessage = useCallback((timetoken: string): ChatMessage | undefined => {
    return enableMessageCaching ? messageCacheRef.current.get(timetoken) : undefined;
  }, [enableMessageCaching]);

  // Optimized typing indicator with debounce
  const sendTypingIndicator = useCallback((isTyping: boolean) => {
    const now = Date.now();

    // Don't send if same state and within debounce period
    if (isTyping === pendingTypingRef.current &&
      now - lastTypingSentRef.current < typingDebounceMs) {
      return;
    }

    pendingTypingRef.current = isTyping;
    lastTypingSentRef.current = now;

    // Use requestIdleCallback for non-critical operations if available
    const scheduleTyping = (typeof requestIdleCallback !== 'undefined'
      ? requestIdleCallback
      : (cb: IdleRequestCallback) => setTimeout(cb, 0));

    scheduleTyping(() => {
      pubnub.signal({
        channel: typingChannel,
        message: {
          typing: isTyping,
          timestamp: now,
          userId: currentUserId
        } as any,
      }).catch((err) => {
        console.warn("[PubNub] Failed to send typing indicator:", err);
        if (err.status?.statusCode === 403) {
          setUiState(prev => ({
            ...prev,
            error: "Permission denied for typing indicator"
          }));
        }
        pendingTypingRef.current = false;
      });
    });
  }, [pubnub, typingChannel, typingDebounceMs, currentUserId]);

  // Batch message updates
  const addMessages = useCallback((newMessages: ChatMessage[]) => {
    setMessages(prev => {
      const existingIds = new Set(prev.map(m => m.id));
      const existingTimetokens = new Set(prev.map(m => m.timetoken).filter(Boolean));

      const uniqueNew = newMessages.filter(m =>
        !existingIds.has(m.id) && (!m.timetoken || !existingTimetokens.has(m.timetoken))
      );

      // Update cache
      uniqueNew.forEach(cacheMessage);

      return [...prev, ...uniqueNew].sort((a, b) =>
        (a.timestamp || 0) - (b.timestamp || 0)
      );
    });
  }, [cacheMessage]);

  // Update specific message
  const updateMessage = useCallback((messageId: string, updater: (message: ChatMessage) => ChatMessage) => {
    setMessages(prev => {
      const index = prev.findIndex(m => m.id === messageId);
      if (index === -1) return prev;

      const updated = [...prev];
      updated[index] = updater(prev[index]);

      // Update cache if timetoken exists
      if (updated[index].timetoken) {
        cacheMessage(updated[index]);
      }

      return updated;
    });
  }, [cacheMessage]);

  // Update message status
  const updateMessageStatus = useCallback((messageId: string, status: any, timetoken?: string) => {
    updateMessage(messageId, (msg) => ({
      ...msg,
      status,
      ...(timetoken && { timetoken })
    }));
  }, [updateMessage]);

  // Batch process delivery receipts to avoid infinite loops
  const processDeliveredReceiptsBatch = useCallback(() => {
    if (pendingReceiptsBatchRef.current.size === 0) return;

    const receiptsToProcess = Array.from(pendingReceiptsBatchRef.current);
    pendingReceiptsBatchRef.current.clear();

    receiptsToProcess.forEach(messageTimetoken => {
      const key = `delivered-${messageTimetoken}`;

      // Skip if already sent or being processed
      if (sentReceiptsRef.current.has(key) || pendingReceiptsRef.current.has(key)) {
        return;
      }

      pendingReceiptsRef.current.add(key);

      pubnub.addMessageAction({
        channel: channelId,
        messageTimetoken,
        action: { type: "receipt", value: "delivered" },
      }).catch((err: any) => {
        if (err?.status?.statusCode !== 409) { // 409 = already exists
          console.warn("[PubNub] Failed to send delivered receipt:", err);
          pendingReceiptsRef.current.delete(key);
        } else {
          // Mark as sent even if it already exists
          sentReceiptsRef.current.add(key);
          pendingReceiptsRef.current.delete(key);
        }
      }).then(() => {
        // Mark as sent on success
        sentReceiptsRef.current.add(key);
        pendingReceiptsRef.current.delete(key);
      });
    });
  }, [pubnub, channelId]);

  // Queue delivery receipt for batching
  const queueDeliveredReceipt = useCallback((messageTimetoken: string) => {
    if (!autoSendDeliveredReceipts) return;

    const key = `delivered-${messageTimetoken}`;

    // Skip if already sent or being processed
    if (sentReceiptsRef.current.has(key) || pendingReceiptsRef.current.has(key)) {
      return;
    }

    // Skip if we've already processed this message
    if (processedMessageTimetokensRef.current.has(messageTimetoken)) {
      return;
    }

    processedMessageTimetokensRef.current.add(messageTimetoken);
    pendingReceiptsBatchRef.current.add(messageTimetoken);

    // Clear existing timeout and set new one for batching
    if (receiptsBatchTimeoutRef.current) {
      clearTimeout(receiptsBatchTimeoutRef.current);
    }

    receiptsBatchTimeoutRef.current = setTimeout(() => {
      processDeliveredReceiptsBatch();
    }, DELIVERY_RECEIPT_BATCH_MS);
  }, [autoSendDeliveredReceipts, processDeliveredReceiptsBatch]);

  // Handle incoming messages
  const handleMessage = useCallback((event: PubNub.MessageEvent) => {
    if (event.channel !== channelId) return;

    const messageData = event.message as ChatMessage;
    const messageTimetoken = String(event.timetoken);

    // Skip raw PubNub file events (auto-published by sendFile).
    // They lack our custom fields (id, senderId) — the real message is
    // published separately via pubnub.publish() with the full ChatMessage.
    if (!messageData.id || !messageData.senderId) return;

    requestAnimationFrame(() => {
      setMessages((prev) => {
        const existingIndex = prev.findIndex(m => m.id === messageData.id);

        if (existingIndex >= 0) {
          // Update existing message
          const existing = prev[existingIndex];
          if (!existing.timetoken && event.timetoken) {
            const updated = [...prev];
            updated[existingIndex] = {
              ...existing,
              timetoken: event.timetoken,
              status: "delivered"
            };
            cacheMessage(updated[existingIndex]);
            return updated;
          }
          return prev;
        }

        // Add new message
        const newMessage = {
          ...messageData,
          status: "delivered" as const,
          timetoken: event.timetoken
        };

        cacheMessage(newMessage);

        // Queue delivery receipt for this message (if from other user)
        if (newMessage.senderId !== currentUserId) {
          queueDeliveredReceipt(messageTimetoken);
          // The receiver has this channel open — mark as read on the backend
          // so unread_count resets and won't reappear after reload
          readChatService(channelId).catch(() => {});
        }

        return [...prev, newMessage];
      });
    });
  }, [channelId, cacheMessage, currentUserId, queueDeliveredReceipt]);

  // Handle presence events (from global-presence channel)
  const handlePresence = useCallback((event: PubNub.PresenceEvent) => {
    if (event.channel !== CHANNEL_PREFIXES.GLOBAL_PRESENCE || event.uuid !== targetUserId) return;

    requestAnimationFrame(() => {
      setUiState(prev => ({
        ...prev,
        isTargetOnline: event.action === "join" || event.action === "state-change"
      }));
    });
  }, [targetUserId]);

  // Handle typing signals
  const handleSignal = useCallback((event: PubNub.SignalEvent) => {
    if (event.channel !== typingChannel || event.publisher === currentUserId) return;

    const signal = event.message as any;

    // Ignore old signals
    if (signal.timestamp && Date.now() - signal.timestamp > TYPING_TIMEOUT_MS) return;

    requestAnimationFrame(() => {
      setUiState(prev => ({ ...prev, isTargetTyping: signal.typing }));
    });

    // Clear typing indicator after timeout
    if (signal.typing) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        setUiState(prev => ({ ...prev, isTargetTyping: false }));
      }, TYPING_TIMEOUT_MS);
    }
  }, [typingChannel, currentUserId]);

  // Handle message actions (reactions, receipts, edits, deletes)
  const handleMessageAction = useCallback((event: any) => {
    if (event.channel !== channelId) return;

    const { messageTimetoken, type, value, uuid, actionTimetoken } = event.data;
    const messageTimetokenStr = String(messageTimetoken);

    // Prevent duplicate processing
    const requestKey = `${type}-${messageTimetoken}-${actionTimetoken}`;
    if (pendingRequestsRef.current.has(requestKey)) return;
    pendingRequestsRef.current.add(requestKey);

    requestAnimationFrame(() => {
      try {
        switch (type) {
          case "reaction":
            if (event.event === "added") {
              setReactions((prev) => {
                const existing = prev[messageTimetokenStr] || [];
                if (existing.some((r) => r.actionTimetoken === actionTimetoken)) {
                  return prev;
                }
                return {
                  ...prev,
                  [messageTimetokenStr]: [...existing, {
                    emoji: value,
                    userId: uuid,
                    actionTimetoken
                  }],
                };
              });
            } else if (event.event === "removed") {
              setReactions((prev) => {
                const existing = prev[messageTimetokenStr] || [];
                return {
                  ...prev,
                  [messageTimetokenStr]: existing.filter((r) => r.actionTimetoken !== actionTimetoken),
                };
              });
            }
            break;

          case "receipt":
            if (event.event === "added") {
              setReceipts((prev) => {
                const existing = prev[messageTimetokenStr] || {};
                const list = (existing[value] || []) as MessageReceipt[];
                if (list.some((r) => r.actionTimetoken === actionTimetoken)) {
                  return prev;
                }
                return {
                  ...prev,
                  [messageTimetokenStr]: {
                    ...existing,
                    [value]: [...list, {
                      type: value as 'delivered' | 'read',
                      userId: uuid,
                      actionTimetoken
                    }],
                  },
                };
              });

              // Mark receipt as sent in our tracking
              if (uuid === currentUserId) {
                sentReceiptsRef.current.add(`${value}-${messageTimetokenStr}`);
              }
            }
            break;

          case "edited":
            if (event.event === "added") {
              try {
                const editData = JSON.parse(value);
                setMessages((prev) => {
                  const index = prev.findIndex(m => String(m.timetoken) === messageTimetokenStr);
                  if (index === -1) return prev;

                  const updated = [...prev];
                  updated[index] = {
                    ...updated[index],
                    text: editData.text,
                    isEdited: true,
                    editedAt: editData.editedAt
                  };
                  cacheMessage(updated[index]);
                  return updated;
                });
              } catch (e) {
                console.warn("[PubNub] Failed to parse edit data:", e);
              }
            }
            break;

          case "deleted":
            if (event.event === "added") {
              setMessages((prev) => {
                const index = prev.findIndex(m => String(m.timetoken) === messageTimetokenStr);
                if (index === -1) return prev;

                const updated = [...prev];
                updated[index] = {
                  ...updated[index],
                  isDeleted: true,
                  deletedBy: uuid,
                  text: ""
                };
                cacheMessage(updated[index]);
                return updated;
              });
            }
            break;

          case "deletedForMe":
            if (event.event === "added" && uuid === currentUserId) {
              setDeletedForMe((prev) => new Set([...prev, value]));
            }
            break;
        }
      } finally {
        // Clean up pending request after processing
        setTimeout(() => {
          pendingRequestsRef.current.delete(requestKey);
        }, 1000);
      }
    });
  }, [channelId, currentUserId, cacheMessage]);

  // Status handler
  const handleStatus = useCallback((event: PubNub.StatusEvent) => {
    requestAnimationFrame(() => {
      setUiState(prev => {
        switch (event.category) {
          case "PNConnectedCategory":
            return { ...prev, isConnected: true, error: null };
          case "PNNetworkDownCategory":
            return { ...prev, isConnected: false, error: "Connection lost" };
          case "PNNetworkUpCategory":
            return { ...prev, isConnected: true, error: null };
          case "PNAccessDeniedCategory": {
            // Only log — don't block the chat. Token may lack global-presence grant.
            const deniedChannels = (event as any)?.errorData?.payload?.channels || [];
            console.warn("[PubNub] Access denied for:", deniedChannels);
            return prev;
          }
          default:
            return prev;
        }
      });
    });
  }, []);

  // Process history messages with caching
  const processHistoryMessages = useCallback((historyMessages: any[]) => {
    const formattedMessages: ChatMessage[] = [];
    const reactionsMap: MessageReactionsMap = {};
    const receiptsMap: MessageReceiptsMap = {};
    const deletedForMeSet: Set<string> = new Set();

    for (const item of historyMessages) {
      const timetoken = String(item.timetoken);

      // Check cache first
      const cached = getCachedMessage(timetoken);
      if (cached) {
        formattedMessages.push(cached);
        continue;
      }

      // Skip raw PubNub file events that lack our custom fields
      if (!item.message.id || !item.message.senderId) continue;

      // Derive timestamp from PubNub timetoken (µs) when message has no timestamp
      const derivedTimestamp =
        item.message.timestamp ||
        Math.floor(Number(BigInt(item.timetoken) / BigInt(10000)));

      const msg: ChatMessage = {
        id: item.message.id,
        text: item.message.text || "",
        senderId: item.message.senderId,
        senderName: item.message.senderName || "Unknown",
        timestamp: derivedTimestamp,
        channelId: item.channel,
        type: item.message.type || "text",
        status: "delivered",
        timetoken: item.timetoken,
        replyTo: item.message.replyTo,
        file: item.message.file,
        files: item.message.files,
        metadata: item.message.metadata
      };

      // Mark this message as processed to avoid duplicate receipts
      if (msg.senderId !== currentUserId) {
        processedMessageTimetokensRef.current.add(timetoken);
      }

      // Process actions
      if (item.actions) {
        // Process edits
        if (item.actions.edited) {
          const editValues = Object.keys(item.actions.edited);
          if (editValues.length > 0) {
            try {
              const editData = JSON.parse(editValues[editValues.length - 1]);
              msg.text = editData.text;
              msg.isEdited = true;
              msg.editedAt = editData.editedAt;
            } catch (e) {
              console.warn("[PubNub] Failed to parse edit:", e);
            }
          }
        }

        // Process deletions
        if (item.actions.deleted) {
          msg.isDeleted = true;
          msg.text = "";
          const deleteActions = Object.values(item.actions.deleted).flat() as any[];
          if (deleteActions.length > 0) {
            msg.deletedBy = deleteActions[0].uuid;
          }
        }

        // Process delete for me
        if (item.actions.deletedForMe) {
          const actions = Object.values(item.actions.deletedForMe).flat() as any[];
          if (actions.some((a: any) => a.uuid === currentUserId)) {
            deletedForMeSet.add(msg.id);
          }
        }

        // Process reactions
        if (item.actions.reaction) {
          const messageReactions: MessageReaction[] = [];
          Object.entries(item.actions.reaction).forEach(([emoji, actions]: [string, any]) => {
            actions.forEach((action: any) => {
              messageReactions.push({
                emoji,
                userId: action.uuid,
                actionTimetoken: action.actionTimetoken,
              });
            });
          });
          if (messageReactions.length > 0) {
            reactionsMap[item.timetoken] = messageReactions;
          }
        }

        // Process receipts - mark as sent in our tracking
        if (item.actions.receipt) {
          const receiptObj: Record<string, MessageReceipt[]> = {};
          Object.entries(item.actions.receipt).forEach(([receiptType, actions]: [string, any]) => {
            receiptObj[receiptType] = actions.map((action: any) => {
              // Mark as sent if it's our own receipt
              if (action.uuid === currentUserId) {
                sentReceiptsRef.current.add(`${receiptType}-${item.timetoken}`);
              }
              return {
                type: receiptType as 'delivered' | 'read',
                userId: action.uuid,
                actionTimetoken: action.actionTimetoken,
              };
            });
          });
          if (Object.keys(receiptObj).length > 0) {
            receiptsMap[item.timetoken] = receiptObj;
          }
        }
      }

      // Cache the processed message
      cacheMessage(msg);
      formattedMessages.push(msg);
    }

    return { formattedMessages, reactionsMap, receiptsMap, deletedForMeSet };
  }, [currentUserId, getCachedMessage, cacheMessage]);

  // Fetch history with abort controller
  const fetchHistory = useCallback(async (options: { count: number; start?: string }) => {
    cleanupAbortController();
    abortControllerRef.current = new AbortController();

    setUiState(prev => ({ ...prev, isLoading: true, hasMore: true }));
    oldestTimetokenRef.current = null;

    try {
      const response = await pubnub.fetchMessages({
        channels: [channelId],
        count: options.count,
        start: options.start,
        includeMeta: true,
        includeMessageActions: true,
      });

      const historyMessages = (response.channels[channelId] || []) as any[];

      setUiState(prev => ({
        ...prev,
        isLoading: false,
        hasMore: historyMessages.length >= options.count,
        error: null
      }));

      const { formattedMessages, reactionsMap, receiptsMap, deletedForMeSet } =
        processHistoryMessages(historyMessages);

      // Always update state (handles both empty and non-empty history)
      setMessages(formattedMessages);
      setReactions(reactionsMap);
      setReceipts(receiptsMap);
      setDeletedForMe(deletedForMeSet);

      if (historyMessages.length > 0) {
        oldestTimetokenRef.current = String(historyMessages[0].timetoken);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;

      console.warn("[PubNub] Failed to fetch history:", err);
      setUiState(prev => ({
        ...prev,
        isLoading: false,
        hasMore: false,
        error: err?.message || "Failed to fetch history"
      }));
    }
  }, [pubnub, channelId, processHistoryMessages, cleanupAbortController]);

  // Load more messages with pagination
  const loadMoreMessages = useCallback(async (): Promise<void> => {
    if (uiState.isLoadingMore || !uiState.hasMore || !oldestTimetokenRef.current) {
      return;
    }

    setUiState(prev => ({ ...prev, isLoadingMore: true }));

    try {
      const response = await pubnub.fetchMessages({
        channels: [channelId],
        count: PAGE_SIZE,
        start: oldestTimetokenRef.current,
        includeMeta: true,
        includeMessageActions: true,
      });

      const historyMessages = (response.channels[channelId] || []) as any[];

      if (historyMessages.length === 0) {
        setUiState(prev => ({ ...prev, isLoadingMore: false, hasMore: false }));
        return;
      }

      // Update oldest timetoken
      oldestTimetokenRef.current = String(historyMessages[0].timetoken);

      const { formattedMessages, reactionsMap, receiptsMap, deletedForMeSet } =
        processHistoryMessages(historyMessages);

      // Batch all state updates
      requestAnimationFrame(() => {
        setMessages(prev => {
          const existingTimetokens = new Set(prev.map(m => m.timetoken).filter(Boolean));
          const newMessages = formattedMessages.filter(m =>
            m.timetoken && !existingTimetokens.has(m.timetoken)
          );
          return [...newMessages, ...prev];
        });

        setReactions(prev => ({ ...prev, ...reactionsMap }));
        setReceipts(prev => ({ ...prev, ...receiptsMap }));
        setDeletedForMe(prev => new Set([...prev, ...deletedForMeSet]));

        setUiState(prev => ({
          ...prev,
          isLoadingMore: false,
          hasMore: historyMessages.length >= PAGE_SIZE
        }));
      });

    } catch (err: any) {
      console.error("[PubNub] Failed to load more messages:", err);
      setUiState(prev => ({
        ...prev,
        isLoadingMore: false,
        hasMore: false,
        error: err?.message || "Failed to load more messages"
      }));
    }
  }, [pubnub, channelId, uiState.isLoadingMore, uiState.hasMore, processHistoryMessages]);

  // Load messages to specific timetoken (for scrolling to a message)
  const loadMessagesToTimetoken = useCallback(async (targetTimetoken: string): Promise<boolean> => {
    // Already loaded?
    const alreadyLoaded = messages.some(
      (m) => String(m.timetoken) === String(targetTimetoken)
    );
    if (alreadyLoaded) return true;

    let cursor: string | undefined = oldestTimetokenRef.current || undefined;
    let found = false;
    let attempts = 0;

    const endToken = (BigInt(targetTimetoken) - BigInt(1)).toString();

    while (!found && attempts < MAX_LOAD_ATTEMPTS) {
      attempts++;

      try {
        const fetchParams: any = {
          channels: [channelId],
          count: PAGE_SIZE,
          end: endToken,
          includeMeta: true,
          includeMessageActions: true,
        };

        if (cursor) fetchParams.start = cursor;

        const response = await pubnub.fetchMessages(fetchParams);
        const historyMessages = (response.channels[channelId] || []) as any[];

        if (historyMessages.length === 0) {
          break;
        }

        const oldestFetched = String(historyMessages[0].timetoken);
        cursor = oldestFetched;

        if (!oldestTimetokenRef.current ||
          BigInt(oldestFetched) < BigInt(oldestTimetokenRef.current)) {
          oldestTimetokenRef.current = oldestFetched;
        }

        const { formattedMessages, reactionsMap, receiptsMap, deletedForMeSet } =
          processHistoryMessages(historyMessages);

        // Batch updates
        setMessages((prev) => {
          const existing = new Set(prev.map((m) => String(m.timetoken)));
          const newMsgs = formattedMessages.filter(
            (m) => m.timetoken && !existing.has(String(m.timetoken))
          );
          return [...newMsgs, ...prev];
        });

        setReactions((prev) => ({ ...prev, ...reactionsMap }));
        setReceipts((prev) => ({ ...prev, ...receiptsMap }));
        setDeletedForMe((prev) => new Set([...prev, ...deletedForMeSet]));

        // Check if target message found
        if (historyMessages.some(
          (h: any) => String(h.timetoken) === String(targetTimetoken)
        )) {
          found = true;
        }

        if ((response as any).more?.start) {
          cursor = (response as any).more.start;
        }
      } catch (err) {
        console.error("[PubNub] Failed to load messages to timetoken:", err);
        break;
      }
    }

    return found;
  }, [pubnub, channelId, messages, processHistoryMessages]);

  // Send message — supports multiple files + reply
  const sendMessage = useCallback(async (text: string, files?: File[] | FileWithPreview[], replyTo?: ReplyTo): Promise<void> => {
    // Normalize files: extract raw File objects if FileWithPreview is passed
    const rawFiles: File[] = (files || []).map((f: any) => (f instanceof File ? f : f.file)).filter(Boolean);

    if (!text.trim() && rawFiles.length === 0) return;

    const messageId = generateMessageId();
    const timestamp = Date.now();

    // ---------- Upload files to PubNub (sequentially to avoid race conditions) ----------
    const uploadedFiles: ChatFileData[] = [];

    if (rawFiles.length > 0) {
      for (const file of rawFiles) {
        try {
          const sendFileResponse = await pubnub.sendFile({
            channel: channelId,
            file: file,
            message: {
              text: rawFiles.length === 1 ? text.trim() : "",
              type: "file",
            },
            storeInHistory: true,
          });

          const fileId = (sendFileResponse as any).id;
          const fileName = (sendFileResponse as any).name;
          const fileTimetoken = (sendFileResponse as any).timetoken;

          if (!fileId || !fileName) {
            throw new Error("Invalid file upload response");
          }

          uploadedFiles.push({
            id: fileId,
            name: fileName,
            timetoken: fileTimetoken,
            type: file.type,
            size: file.size,
            // url intentionally omitted — fetched via downloadFile for security
          });
        } catch (err: any) {
          console.error(`[PubNub] File upload failed for ${file.name}:`, err);
          setUiState(prev => ({
            ...prev,
            error: `Failed to upload ${file.name}`,
          }));
          throw err;
        }
      }
    }

    // ---------- Build message object ----------
    const hasFiles = uploadedFiles.length > 0;

    const message: ChatMessage = {
      id: messageId,
      text: text.trim(),
      senderId: currentUserId,
      senderName: currentUserName,
      timestamp,
      channelId,
      type: hasFiles ? "file" : "text",
      status: "sending",
      ...(replyTo && { replyTo }),
      // Single-file backward compat + multi-file
      ...(hasFiles && { file: uploadedFiles[0] }),
      ...(uploadedFiles.length > 1 && { files: uploadedFiles }),
    };

    // Optimistic update
    addMessages([message]);
    sendTypingIndicator(false);

    try {
      const publishResponse = await pubnub.publish({
        channel: channelId,
        message,
        storeInHistory: true,
      });
      updateMessageStatus(messageId, "sent", String(publishResponse.timetoken));

      // Backend sync (fire-and-forget) — send all files
      sendMessageService(message, rawFiles).catch(console.warn);
    } catch (err: any) {
      console.error("[PubNub] Failed to publish message:", err);

      setUiState(prev => ({
        ...prev,
        error: err?.status?.statusCode === 403
          ? "Access denied. Please check your PubNub keys."
          : "Failed to send message",
      }));

      updateMessageStatus(messageId, "failed");
      throw err;
    }
  }, [pubnub, channelId, currentUserId, currentUserName, generateMessageId, addMessages, sendTypingIndicator, updateMessageStatus]);

  // Retry failed message
  const retryFailedMessage = useCallback(async (messageId: string): Promise<void> => {
    const failedMessage = messages.find(m => m.id === messageId && m.status === "failed");
    if (!failedMessage) return;

    updateMessageStatus(messageId, "sending");

    try {
      const response = await pubnub.publish({
        channel: channelId,
        message: failedMessage,
        storeInHistory: true,
      });

      updateMessageStatus(messageId, "sent", String(response.timetoken));
      clearError();

    } catch (err: any) {
      console.error("[PubNub] Failed to retry message:", err);
      updateMessageStatus(messageId, "failed");
      setUiState(prev => ({
        ...prev,
        error: "Failed to retry message"
      }));
    }
  }, [pubnub, channelId, messages, updateMessageStatus, clearError]);

  // Edit message
  const editMessage = useCallback(async (messageId: string, newText: string): Promise<void> => {
    if (!newText.trim()) return;

    const targetMessage = messages.find(
      (m) => m.id === messageId && m.senderId === currentUserId && !m.isDeleted
    );

    if (!targetMessage?.timetoken) {
      console.error("[PubNub] Cannot edit: message not found or no timetoken");
      return;
    }

    const editedAt = Date.now();
    const messageTimetoken = String(targetMessage.timetoken);
    const originalText = targetMessage.text;

    // Optimistic update
    updateMessage(messageId, (msg) => ({
      ...msg,
      text: newText.trim(),
      isEdited: true,
      editedAt
    }));

    try {
      await pubnub.addMessageAction({
        channel: channelId,
        messageTimetoken,
        action: {
          type: "edited",
          value: JSON.stringify({ text: newText.trim(), editedAt }),
        },
      });
    } catch (err: any) {
      console.error("[PubNub] Failed to edit message:", err);

      // Revert optimistic update
      updateMessage(messageId, (msg) => ({
        ...msg,
        text: originalText,
        isEdited: targetMessage.isEdited,
        editedAt: targetMessage.editedAt
      }));

      setUiState(prev => ({
        ...prev,
        error: "Failed to edit message"
      }));
    }
  }, [pubnub, channelId, currentUserId, messages, updateMessage]);

  // Delete message
  const deleteMessage = useCallback(async (messageId: string, deleteForEveryone: boolean): Promise<void> => {
    const targetMessage = messages.find((m) => m.id === messageId);

    if (!targetMessage?.timetoken) {
      console.error("[PubNub] Cannot delete: message not found or no timetoken");
      return;
    }

    const messageTimetoken = String(targetMessage.timetoken);

    if (deleteForEveryone) {
      // Only allow deleting own messages for everyone
      if (targetMessage.senderId !== currentUserId) return;

      // Optimistic update
      updateMessage(messageId, (msg) => ({
        ...msg,
        isDeleted: true,
        deletedBy: currentUserId,
        text: ""
      }));

      try {
        await pubnub.addMessageAction({
          channel: channelId,
          messageTimetoken,
          action: { type: "deleted", value: "true" },
        });
      } catch (err: any) {
        console.error("[PubNub] Failed to delete message:", err);

        // Revert optimistic update
        updateMessage(messageId, (msg) => ({
          ...msg,
          isDeleted: false,
          deletedBy: undefined,
          text: targetMessage.text
        }));

        setUiState(prev => ({
          ...prev,
          error: "Failed to delete message"
        }));
      }
    } else {
      // Delete for me only
      setDeletedForMe((prev) => new Set([...prev, messageId]));

      try {
        await pubnub.addMessageAction({
          channel: channelId,
          messageTimetoken,
          action: {
            type: "deletedForMe",
            value: messageId,
          },
        });
      } catch (err: any) {
        console.error("[PubNub] Failed to delete message for me:", err);

        // Revert optimistic update
        setDeletedForMe((prev) => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });
      }
    }
  }, [pubnub, channelId, currentUserId, messages, updateMessage]);

  // Add reaction (one reaction per user per message — toggle/swap)
  const addReaction = useCallback(async (messageTimetoken: string, emoji: string): Promise<void> => {
    const existing = (reactions[messageTimetoken] || []).find(
      (r) => r.userId === currentUserId,
    );

    // Same emoji → toggle off (remove it)
    if (existing?.emoji === emoji) {
      setReactions((prev) => ({
        ...prev,
        [messageTimetoken]: (prev[messageTimetoken] || []).filter(
          (r) => r.actionTimetoken !== existing.actionTimetoken,
        ),
      }));
      try {
        await pubnub.removeMessageAction({
          channel: channelId,
          messageTimetoken,
          actionTimetoken: existing.actionTimetoken,
        });
      } catch {
        setReactions((prev) => ({
          ...prev,
          [messageTimetoken]: [...(prev[messageTimetoken] || []), existing],
        }));
      }
      return;
    }

    // Different emoji → remove old reaction first, then add new
    if (existing) {
      setReactions((prev) => ({
        ...prev,
        [messageTimetoken]: (prev[messageTimetoken] || []).filter(
          (r) => r.actionTimetoken !== existing.actionTimetoken,
        ),
      }));
      try {
        await pubnub.removeMessageAction({
          channel: channelId,
          messageTimetoken,
          actionTimetoken: existing.actionTimetoken,
        });
      } catch {
        setReactions((prev) => ({
          ...prev,
          [messageTimetoken]: [...(prev[messageTimetoken] || []), existing],
        }));
        return;
      }
    }

    // Add the new reaction
    const tempActionTimetoken = `temp-${Date.now()}-${Math.random()}`;
    setReactions((prev) => ({
      ...prev,
      [messageTimetoken]: [
        ...(prev[messageTimetoken] || []),
        { emoji, userId: currentUserId, actionTimetoken: tempActionTimetoken }
      ],
    }));

    try {
      const response = await pubnub.addMessageAction({
        channel: channelId,
        messageTimetoken,
        action: { type: "reaction", value: emoji },
      });

      setReactions((prev) => ({
        ...prev,
        [messageTimetoken]: (prev[messageTimetoken] || []).map((r) =>
          r.actionTimetoken === tempActionTimetoken
            ? { ...r, actionTimetoken: response.data.actionTimetoken }
            : r
        ),
      }));
    } catch (err: any) {
      console.error("[PubNub] Failed to add reaction:", err);
      setReactions((prev) => ({
        ...prev,
        [messageTimetoken]: (prev[messageTimetoken] || []).filter(
          (r) => r.actionTimetoken !== tempActionTimetoken
        ),
      }));
    }
  }, [pubnub, channelId, currentUserId, reactions]);

  // Remove reaction
  const removeReaction = useCallback(async (messageTimetoken: string, actionTimetoken: string): Promise<void> => {
    const removedReaction = reactions[messageTimetoken]?.find(
      (r) => r.actionTimetoken === actionTimetoken
    );

    // Optimistic update
    setReactions((prev) => ({
      ...prev,
      [messageTimetoken]: (prev[messageTimetoken] || []).filter(
        (r) => r.actionTimetoken !== actionTimetoken
      ),
    }));

    try {
      await pubnub.removeMessageAction({
        channel: channelId,
        messageTimetoken,
        actionTimetoken,
      });
    } catch (err: any) {
      console.error("[PubNub] Failed to remove reaction:", err);

      // Restore reaction on failure
      if (removedReaction) {
        setReactions((prev) => ({
          ...prev,
          [messageTimetoken]: [...(prev[messageTimetoken] || []), removedReaction],
        }));
      }

      setUiState(prev => ({
        ...prev,
        error: "Failed to remove reaction"
      }));
    }
  }, [pubnub, channelId, reactions]);

  // Mark as read
  const markAsRead = useCallback((messageTimetoken: string) => {
    const key = `read-${messageTimetoken}`;
    if (sentReceiptsRef.current.has(key) || pendingReceiptsRef.current.has(key)) return;

    pendingReceiptsRef.current.add(key);

    pubnub.addMessageAction({
      channel: channelId,
      messageTimetoken,
      action: { type: "receipt", value: "read" },
    }).catch((err: any) => {
      if (err?.status?.statusCode !== 409) {
        console.warn("[PubNub] Failed to send read receipt:", err);
        pendingReceiptsRef.current.delete(key);
      } else {
        sentReceiptsRef.current.add(key);
        pendingReceiptsRef.current.delete(key);
      }
    }).then(() => {
      sentReceiptsRef.current.add(key);
      pendingReceiptsRef.current.delete(key);
    });
  }, [pubnub, channelId]);

  // Get message status
  const getMessageStatus = useCallback((msg: ChatMessage): any => {
    if (msg.senderId !== currentUserId) return "delivered";
    if (msg.status === "sending") return "sending";
    if (msg.status === "failed") return "failed";
    if (!msg.timetoken) return "sent";

    const tt = String(msg.timetoken);
    const msgReceipts = receipts[tt];

    if (msgReceipts?.read?.some((r) => r.userId === targetUserId)) {
      return "read";
    }
    if (msgReceipts?.delivered?.some((r) => r.userId === targetUserId)) {
      return "delivered";
    }
    return "sent";
  }, [currentUserId, targetUserId, receipts]);

  // Clear messages
  const clearMessages = useCallback(() => {
    setMessages([]);
    setReactions({});
    setReceipts({});
    setDeletedForMe(new Set());
    messageCacheRef.current.clear();
    oldestTimetokenRef.current = null;
    processedMessageTimetokensRef.current.clear();
    sentReceiptsRef.current.clear();
    pendingReceiptsRef.current.clear();
    pendingReceiptsBatchRef.current.clear();
  }, []);

  // Set hasMore
  const setHasMore = useCallback((value: boolean) => {
    setUiState(prev => ({ ...prev, hasMore: value }));
  }, []);

  // Subscribe to channel
  useEffect(() => {
    if (!autoSubscribe || !currentUserId || !targetUserId) return;

    // Reset all state for a clean slate when switching chats
    setMessages([]);
    setReactions({});
    setReceipts({});
    setDeletedForMe(new Set());
    setUiState({ isLoading: false, isLoadingMore: false, hasMore: true, error: null, isConnected: true, isTargetOnline: false, isTargetTyping: false });
    oldestTimetokenRef.current = null;
    sentReceiptsRef.current.clear();
    pendingReceiptsRef.current.clear();
    pendingReceiptsBatchRef.current.clear();
    processedMessageTimetokensRef.current.clear();
    messageCacheRef.current.clear();

    const listener = {
      message: handleMessage,
      presence: handlePresence,
      signal: handleSignal,
      status: handleStatus,
      messageAction: handleMessageAction,
    };

    pubnub.addListener(listener);

    // Subscribe to channels
    pubnub.subscribe({
      channels: [channelId, typingChannel],
      withPresence: true,
    });

    // Fetch initial history
    fetchHistory({ count: historyCount });

    // Check presence on global channel
    const checkPresence = () => {
      pubnub.hereNow({
        channels: [CHANNEL_PREFIXES.GLOBAL_PRESENCE],
        includeUUIDs: true,
      }).then((response) => {
        const isOnline = response.channels[CHANNEL_PREFIXES.GLOBAL_PRESENCE]?.occupants
          ?.some((o) => o.uuid === targetUserId) ?? false;

        requestAnimationFrame(() => {
          setUiState(prev => ({ ...prev, isTargetOnline: isOnline }));
        });
      }).catch(console.warn);
    };

    const initialCheck = setTimeout(checkPresence, 2000);
    const presenceInterval = setInterval(checkPresence, PRESENCE_CHECK_INTERVAL_MS);

    // Cleanup
    return () => {
      pubnub.removeListener(listener);
      pubnub.unsubscribe({ channels: [typingChannel] });
      clearTimeout(initialCheck);
      clearInterval(presenceInterval);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (receiptsBatchTimeoutRef.current) {
        clearTimeout(receiptsBatchTimeoutRef.current);
      }
      cleanupAbortController();
    };
  }, [
    pubnub, channelId, typingChannel, autoSubscribe,
    currentUserId, targetUserId, historyCount,
    handleMessage, handlePresence, handleSignal,
    handleStatus, handleMessageAction, fetchHistory,
    cleanupAbortController
  ]);

  return {
    messages: messages.filter(m => !deletedForMe.has(m.id)), // Filter out deleted for me
    isLoading: uiState.isLoading,
    isLoadingMore: uiState.isLoadingMore,
    hasMore: uiState.hasMore,
    error: uiState.error,
    isConnected: uiState.isConnected,
    isTargetOnline: uiState.isTargetOnline,
    isTargetTyping: uiState.isTargetTyping,
    reactions,
    receipts,
    deletedForMe,
    sendMessage,
    editMessage,
    deleteMessage,
    sendTypingIndicator,
    addReaction,
    removeReaction,
    markAsRead,
    getMessageStatus,
    loadMoreMessages,
    loadMessagesToTimetoken,
    clearMessages,
    channelId,
    retryFailedMessage,
    clearError,
  };
};

export default useOneToOneChat;